import { createRouter, createWebHistory, type RouteLocation } from 'vue-router'
import { useAuthStore } from '@/store/AuthStore'

// Layouts
import AuthLayout from '@/layouts/AuthLayout.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import ConnectLayout from '@/layouts/ConnectLayout.vue'

// Auth pages
import Login from '@/pages/Login.vue'
import SignUp from '@/pages/SignUp.vue'
import ForgotPassword from '@/pages/ForgotPassword.vue'
import ResetPassword from '@/pages/ResetPassword.vue'
import VerifyEmail from '@/pages/VerifyEmail.vue'
import WelcomePage from '@/pages/WelcomePage.vue'
import Onboarding from '@/pages/Onboarding.vue'

// App pages
import Today from '@/pages/Today.vue'
import Schedule from '@/pages/Schedule.vue'

// Classes pages
import ClassesOverview from '@/pages/ClassesOverview.vue'
import ClassWorkspace from '@/pages/ClassWorkspace.vue'

// Planning pages
import Planning from '@/pages/planning/Planning.vue'

// Connect pages — ConnectLayout handles everything, no child pages needed

// Join page (lazy loaded)
const Join = () => import('@/pages/Join.vue')

// Detail pages (lazy loaded)
// §05/§06 themed detail surfaces (live inside the class workspace)
const LessonDetail = () => import('@/pages/LessonDetail.vue')
const AssignmentDetail = () => import('@/pages/AssignmentDetail.vue')
const StudentDetail = () => import('@/pages/StudentDetail.vue')

const routes = [
  // Auth routes (public)
  {
    path: '/',
    component: AuthLayout,
    children: [
      { path: 'login', name: 'login', component: Login },
      { path: 'register', name: 'register', component: SignUp },
      { path: 'forgot-password', name: 'forgot-password', component: ForgotPassword },
    ],
  },

  // Join page (public — students accept invites here)
  {
    path: '/join/:token',
    component: AuthLayout,
    children: [{ path: '', name: 'join', component: Join }],
  },

  // Legacy /auth/* redirects
  { path: '/auth/login', redirect: '/login' },
  { path: '/auth/register', redirect: '/register' },
  { path: '/auth/register-school', redirect: '/register' },
  { path: '/auth/forgot-password', redirect: '/forgot-password' },

  // Reset password (standalone — accessible whether logged in or not)
  {
    path: '/reset-password',
    component: AuthLayout,
    children: [{ path: '', name: 'reset-password', component: ResetPassword }],
  },

  // Verify email (standalone — accessible whether logged in or not)
  {
    path: '/verify-email',
    component: AuthLayout,
    children: [{ path: '', name: 'verify-email', component: VerifyEmail }],
  },

  // Welcome (standalone — accessible whether logged in or not, linked from welcome email)
  {
    path: '/welcome',
    component: AuthLayout,
    children: [{ path: '', name: 'welcome', component: WelcomePage }],
  },

  // Android web-checkout return paths. Android's intent filters normally
  // intercept these URLs and reopen the app; these routes are the fallback
  // for users on a browser that ignores App Links or with the app uninstalled.
  {
    path: '/upgrade-complete',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'upgrade-complete',
        component: () => import('@/pages/UpgradeComplete.vue'),
      },
    ],
  },
  {
    path: '/upgrade-cancelled',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'upgrade-cancelled',
        component: () => import('@/pages/UpgradeCancelled.vue'),
      },
    ],
  },

  // Get Started - go straight to register
  { path: '/get-started', redirect: '/register' },

  // Legacy redirect
  {
    path: '/auth/reset-password',
    redirect: (to: RouteLocation) => ({ path: '/reset-password', query: to.query }),
  },

  // Legacy /app routes — redirect to new paths (no /app prefix)
  { path: '/app', redirect: '/today' },
  {
    path: '/app/:pathMatch(.*)*',
    redirect: (to: RouteLocation) => '/' + (to.params.pathMatch as string[]).join('/'),
  },

  // Onboarding - for authenticated users without a tenant
  {
    path: '/onboarding',
    name: 'onboarding',
    component: Onboarding,
    meta: { requiresAuth: true, isOnboarding: true },
  },

  // Checkout (authenticated, standalone — no app chrome)
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/pages/Checkout.vue'),
    meta: { requiresAuth: true },
  },

  // App routes (authenticated) — no /app prefix
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      { path: 'today', name: 'today', component: Today, meta: { teacherOnly: true } },
      { path: 'home', redirect: '/today' },
      { path: 'classes', name: 'classes', component: ClassesOverview, meta: { teacherOnly: true } },
      {
        path: 'classes/:classId',
        name: 'class-workspace',
        component: ClassWorkspace,
        meta: { teacherOnly: true },
      },
      {
        path: 'classes/:classId/lesson/:lessonId',
        name: 'lesson-detail',
        component: LessonDetail,
        meta: { teacherOnly: true },
      },
      {
        path: 'classes/:classId/assignment/:assignmentId',
        name: 'assignment-detail',
        component: AssignmentDetail,
        meta: { teacherOnly: true },
      },
      {
        path: 'classes/:classId/student/:studentId',
        name: 'student-detail-class',
        component: StudentDetail,
        meta: { teacherOnly: true },
      },
      { path: 'planning', name: 'planning', component: Planning, meta: { teacherOnly: true } },
      {
        path: 'planning/:id',
        name: 'planning-course',
        component: () => import('@/pages/planning/PlanningCourse.vue'),
        meta: { teacherOnly: true },
      },
      // Lesson plan detail page (standalone, not under scheme layout)
      {
        path: 'planning/lessons/:id',
        name: 'lesson-plan-details',
        component: () => import('@/pages/planning/LessonPlanDetails.vue'),
        meta: { teacherOnly: true },
      },
      { path: 'schedule', name: 'schedule', component: Schedule },

      // Connect pages (available to all roles) — ConnectLayout handles both views
      { path: 'connect', name: 'connect', component: ConnectLayout },
      { path: 'connect/school', name: 'connect-school', component: ConnectLayout },
      { path: 'connect/spaces/:id', name: 'space-detail', component: ConnectLayout },

      // Student detail page (global / neutral context)
      {
        path: 'students/:studentId',
        name: 'student-details',
        component: StudentDetail,
        meta: { teacherOnly: true },
      },

      // Student account page
      { path: 'account', name: 'account', component: () => import('@/pages/AccountSettings.vue') },

      // Settings pages (teacher only)
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/Settings.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/membership',
        name: 'settings-membership',
        component: () => import('@/pages/settings/Membership.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/upgrade',
        name: 'settings-upgrade',
        component: () => import('@/pages/settings/Upgrade.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/school',
        name: 'settings-school',
        component: () => import('@/pages/settings/SchoolSetup.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/academic-years',
        name: 'settings-academic-years',
        component: () => import('@/pages/settings/AcademicYears.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/display',
        name: 'settings-display',
        component: () => import('@/pages/settings/DisplaySettings.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/assessment-categories',
        name: 'settings-assessment-categories',
        component: () => import('@/pages/settings/AssessmentCategories.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/marking-scales',
        name: 'settings-marking-scales',
        component: () => import('@/pages/settings/MarkingScales.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/schedule',
        name: 'settings-schedule',
        component: () => import('@/pages/settings/ScheduleSettings.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/workspace',
        name: 'settings-workspace',
        component: () => import('@/pages/settings/WorkspaceSettings.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/help',
        name: 'settings-help',
        component: () => import('@/pages/settings/HelpFAQ.vue'),
        meta: { teacherOnly: true },
      },
      {
        path: 'settings/billing',
        name: 'settings-billing',
        component: () => import('@/pages/settings/BillingHistory.vue'),
        meta: { teacherOnly: true },
      },
    ],
  },

  // Catch-all — send to login (which redirects to marketing if needed)
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login',
  },
]

/**
 * Build the app router — routes, auth guard and error recovery included.
 * The app uses the default singleton below; tests create isolated instances.
 */
export function createAppRouter(history = createWebHistory()) {
  const router = createRouter({
    history,
    routes,
    scrollBehavior(_to, _from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      return { top: 0 }
    },
  })

  installGuards(router)
  return router
}

type AppRouter = ReturnType<typeof createRouter>

function installGuards(router: AppRouter) {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    // Ensure auth is initialized (tries GET /me with cookie)
    if (!auth.isInitialized) {
      await auth.init()
    }

    // Redirect authenticated users away from login
    if (to.name === 'login' && auth.isAuthenticated) {
      if (auth.needsOnboarding) {
        return { name: 'onboarding' }
      }
      return { path: auth.isStudent ? '/connect' : '/today' }
    }

    // Check if route requires authentication
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      if (!auth.isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } }
      }

      // Check if user needs onboarding (no tenant assigned)
      const isOnboardingRoute = to.matched.some((record) => record.meta.isOnboarding)

      if (auth.currentUser) {
        if (auth.needsOnboarding && !isOnboardingRoute) {
          // User needs onboarding but is trying to access app routes
          return { name: 'onboarding' }
        }

        if (!auth.needsOnboarding && isOnboardingRoute) {
          // User has completed onboarding but is on onboarding page
          return { path: auth.isStudent ? '/connect' : '/today' }
        }

        // Role-based access: students cannot access teacher-only routes
        if (auth.isStudent && to.matched.some((record) => record.meta.teacherOnly)) {
          return { path: '/connect' }
        }
      }
    }

    // Root path — authenticated users go to home/connect, guests go to login
    if (to.path === '/') {
      if (auth.isAuthenticated) {
        if (auth.needsOnboarding) {
          return { name: 'onboarding' }
        }
        return { path: auth.isStudent ? '/connect' : '/today' }
      }
      return { name: 'login' }
    }
  })

  // Recover from stale chunk imports after a deploy / HMR cycle.
  // When Vite invalidates a code-split chunk (rename or rebuild) the browser
  // holds the old hashed URL and the next navigation throws. Reload once so
  // the user lands on the new bundle instead of staring at a broken screen.
  router.onError((error, to) => {
    const message = error instanceof Error ? error.message : String(error)
    const isStaleChunk =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('error loading dynamically imported module')

    if (isStaleChunk && typeof window !== 'undefined') {
      window.location.assign(to.fullPath)
    }
  })
}

const router = createAppRouter()

export default router
